import { DeleteOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useCallback } from 'react';
import { useDrop } from 'react-dnd';
import styled from 'styled-components';

import { CenterFlex, Divider, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Checkbox from '@/components/Checkbox/Checkbox';
import Typo, { SpanStyle } from '@/components/Typography/Typo';
import { useFeedbackTerminology } from '@/entities/workspace';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { radius } from '@/styles/sizes';
import {
  draggableTopicType,
  TAXONOMY_DND_ITEM,
  TOPIC_2_TYPE,
} from '@/types/taxonomyConst';

import { useTaxonomyDrag } from '../../../context/TaxonomyDragContext';
import { useTopicDetailContext } from '../../../context/TopicDetailContext';

type Props = {
  currentTopicType: TOPIC_2_TYPE | null;
  selectedList: ClassifiedFeedbackUnit[];
  topic_alias?: string;
  taxonomyAlias: string;
  onClearListClick: () => void;
  refetchList: () => void;
  selectedCount: number;
  isAllSelected: boolean;
  onCheckAllClick: () => void;
};

const FloatActionMenu = (props: Props) => {
  const color = useColor();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const setPopup = usePopup();
  const addToast = useToast();
  const dragContext = useTaxonomyDrag();
  const { removeItem } = useTopicDetailContext();

  const onUnlinkTopicHanlder = useCallback(() => {
    if (!props.topic_alias) return;
    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: replaceFeedbackTerminology('Unlink feedbacks'),
      message: replaceFeedbackTerminology(
        'Are you sure you want to unlink the selected feedbacks?',
      ),
      onConfirm: async () => {
        if (!props.topic_alias) return;
        await service.taxonomy.unclassifyFeedbackToTopic(props.topic_alias, {
          current_topic_id: props.topic_alias,
          feedbacks: props.selectedList,
          taxonomy_id: props.taxonomyAlias,
        });
        removeItem(props.selectedList);
        addToast(
          replaceFeedbackTerminology(
            'Feedbacks have been unlinked from the topic',
          ),
          'success',
        );
        props.refetchList();
        props.onClearListClick();
      },
    });
  }, [addToast, props, removeItem, replaceFeedbackTerminology, setPopup]);

  const onAddToFilterListHandler = useCallback(() => {
    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Add to exclusion list',
      message: replaceFeedbackTerminology(
        'Are you sure you want to add the selected feedbacks to the exclusion list?',
      ),
      onConfirm: async () => {
        await service.taxonomy.excludeFromAnalysis({
          current_topic_id: props.topic_alias,
          feedbacks: props.selectedList,
          taxonomy_id: props.taxonomyAlias,
        });
        removeItem(props.selectedList);
        addToast(
          replaceFeedbackTerminology(
            'Feedbacks have been added to the exclusion list',
          ),
          'success',
        );
        props.refetchList();
        props.onClearListClick();
      },
    });
  }, [addToast, props, removeItem, replaceFeedbackTerminology, setPopup]);

  const [{ isOver: unlinkIsOver }, unlinkeDropzone] = useDrop(
    () => ({
      accept: TAXONOMY_DND_ITEM,
      drop(item) {
        if (!dragContext.isDragging) return;
        if (props.currentTopicType === TOPIC_2_TYPE.RULE || !props.topic_alias)
          return;
        onUnlinkTopicHanlder();
      },
      collect(monitor) {
        return {
          isOver: monitor.isOver(),
        };
      },
    }),
    [dragContext.isDragging, onAddToFilterListHandler],
  );

  const [{ isOver: addToFilterOver }, addtoFilterDropzone] = useDrop(
    () => ({
      accept: TAXONOMY_DND_ITEM,
      drop(item) {
        if (!dragContext.isDragging) return;
        onAddToFilterListHandler();
      },
      collect(monitor) {
        return {
          isOver: monitor.isOver(),
        };
      },
    }),
    [dragContext.isDragging, onUnlinkTopicHanlder],
  );

  return (
    <Style>
      {props.currentTopicType &&
        draggableTopicType.includes(props.currentTopicType) && (
          <MessageArea>
            {replaceFeedbackTerminology(
              'You can drag and drop to move feedback to other topics',
            )}
          </MessageArea>
        )}
      <Flex g={12}>
        <Flex g={12} a="center">
          <Checkbox
            checked={props.isAllSelected}
            halfCheck={!props.isAllSelected}
            onChange={props.onCheckAllClick}
          />
          <Typo>{props.selectedCount} selected</Typo>
        </Flex>
        <Divider direction="v" />
        <Button
          leftIcon={<DisconnectOutlined />}
          variant="outlined"
          sizeType="s"
          onClick={onUnlinkTopicHanlder}
          style={{
            backgroundColor: color.gray100,
            borderColor: unlinkIsOver ? color.primary100 : color.gray400,
          }}
          disabled={
            props.currentTopicType === TOPIC_2_TYPE.RULE || !props.topic_alias
          }
          ref={unlinkeDropzone}
        >
          Unlink from this topic
        </Button>
        <Button
          leftIcon={<DeleteOutlined />}
          variant="outlined"
          sizeType="s"
          style={{
            backgroundColor: color.gray100,
            borderColor: addToFilterOver ? color.primary100 : color.gray400,
          }}
          onClick={onAddToFilterListHandler}
          ref={addtoFilterDropzone}
        >
          Add to exclusion list
        </Button>
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  width: fit-content;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: ${radius.card}px;
  background-color: ${(props) => props.theme.colors.gray100};
  border: 1px solid ${(props) => props.theme.colors.primary100};
  & > * {
    flex-shrink: 0;
  }
`;

const MessageArea = styled(CenterFlex)`
  width: 100%;
  padding: 6px;
  flex: 1;
  background-color: ${(props) =>
    getAlphaColor(props.theme.colors.primary100, 0.1)};
  color: ${(props) => props.theme.colors.primary100};
  border-radius: ${radius.token.sm}px;
  ${SpanStyle({
    type: 'label3',
    color: 'primary100',
  })}
`;

export default FloatActionMenu;
